import React, { useEffect, useState } from 'react';
import { Button, Col, Row, Tooltip, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { DeleteOutlined, ExclamationCircleOutlined, FormOutlined } from '@ant-design/icons';

import UpdateGatewayDrawer from '../UpdateGatewayDrawer/UpdateGatewayDrawer';
import AppTable from '../../../../components/AppTable/AppTable';
import { Gateway } from '../../../../redux/reducers/clients/clients.types';
import {
    fetchGatewaysRequest,
    deleteGatewayRequest,
    fetchSensorsRequest,
} from '../../../../redux/reducers/clients/clientsSlice';
import { RootState } from '../../../../redux/store';
import AddGatewayModal from '../AddGatewayModal/AddGatewayModal';

interface GatewaysTableProps {
    clientId: string;
}

const GatewaysTable: React.FC<GatewaysTableProps> = ({ clientId }) => {
    const { t } = useTranslation('clients');
    const dispatch = useDispatch();

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedGatewayId, setSelectedGatewayId] = useState<string | undefined>();

    const { data: gateways, loading } = useSelector((state: RootState) => state.clients.gateways);
    const { data: sensors } = useSelector((state: RootState) => state.clients.sensors);

    const handleEditGateway = (gatewayId: string) => {
        setSelectedGatewayId(gatewayId);
        setIsDrawerOpen(true);
    };

    const handleDeleteGateway = (gatewayId: string) => {
        Modal.confirm({
            title: t('are_you_sure_delete_gateway'),
            icon: <ExclamationCircleOutlined />,
            content: t('this_action_cannot_be_undone'),
            okText: t('delete'),
            cancelText: t('cancel'),
            onOk: () => {
                dispatch(deleteGatewayRequest({ clientId, gatewayId }));
            },
        });
    };

    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
        setSelectedGatewayId(undefined);
    };

    const columns = [
        {
            title: <span className="base-table-text">{t('gsm_number')}</span>,
            dataIndex: 'gsm',
            key: 'gsm',
        },
        {
            title: <span className="base-table-text">{t('gateway')}</span>,
            dataIndex: 'gateway',
            key: 'gateway',
        },
        {
            title: <span className="base-table-text">{t('sensor_count')}</span>,
            dataIndex: 'sensorCount',
            key: 'sensorCount',
        },
        {
            title: <span className="base-table-text">{t('actions')}</span>,
            key: 'actions',
            align: 'center' as const,
            render: (_: any, record: Gateway) => (
                <Row justify="center" gutter={16}>
                    <Col>
                        <Tooltip title={t('edit')}>
                            <Button type="text" icon={<FormOutlined />} onClick={() => handleEditGateway(record.id)} />
                        </Tooltip>
                    </Col>
                    <Col>
                        <Tooltip title={t('delete')}>
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDeleteGateway(record.id)}
                            />
                        </Tooltip>
                    </Col>
                </Row>
            ),
        },
    ];

    useEffect(() => {
        if (clientId) {
            dispatch(fetchGatewaysRequest(clientId));
        }
    }, [dispatch, clientId]);

    const gatewaysWithSensorCount = gateways?.map((gateway) => ({
        ...gateway,
        sensorCount: sensors?.filter((sensor) => sensor.gatewayId === gateway.id).length || 0,
    }));

    return (
        <>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <Typography.Title style={{ margin: 0 }} level={3}>
                        {t('gateways')}
                    </Typography.Title>
                </Col>
                <Col>
                    <Button type="primary" style={{ backgroundColor: '#02b9d9' }} onClick={() => setIsModalOpen(true)}>
                        {t('add_gateway')}
                    </Button>
                </Col>
            </Row>
            <AppTable
                className="base-table-body"
                loading={loading}
                dataSource={gatewaysWithSensorCount}
                columns={columns}
                paginationConfig={{
                    pageSize: 10,
                    showSizeChanger: false,
                }}
            />
            <UpdateGatewayDrawer
                open={isDrawerOpen}
                onClose={handleDrawerClose}
                gatewayId={selectedGatewayId}
                clientId={clientId}
            />
            <AddGatewayModal visible={isModalOpen} onClose={() => setIsModalOpen(false)} clientId={clientId} />
        </>
    );
};

export default GatewaysTable;